import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useToast } from '@/components/ui';
import { issueAccount } from '@/api/console';
import { applyBulkLevelAssign, OrgAdminApiError } from '@/api/orgAdmin';
import type { ScopedAccountsVM } from './useScopedAccounts';

/**
 * The portal fields a CSV column may be mapped onto. `staffCode` is deliberately
 * ABSENT even though frame 35c lists "Mã nhân sự": `POST /accounts` takes
 * `username` / `email` / `first_name` / `last_name` / `credential_mode` and nothing
 * else, and `Assignment` carries no staff-code field either, so a mapped column
 * would be read, validated and then silently dropped.
 */
export const IMPORT_TARGETS = ['ignore', 'fullName', 'email', 'username', 'levelName'] as const;
export type ImportTarget = (typeof IMPORT_TARGETS)[number];

export type ImportStep = 'upload' | 'map' | 'preview';

export interface ImportRowPlan {
    lineNumber: number;
    fullName: string;
    email: string;
    username: string;
    levelName: string;
    /** Resolved from `levelName` against the scope org's active Levels. */
    levelId: string | null;
    /** Blocking problems — a row with any of these is never sent. */
    problems: string[];
}

export interface ImportRowResult {
    lineNumber: number;
    username: string;
    ok: boolean;
    message: string;
}

/**
 * Hard ceiling on one run, taken from frame 35c's own text: "Trên 200 dòng sẽ chạy
 * nền — theo dõi tiến độ … ở màn việc chạy nền." There is no background-job
 * runner for account imports in portal-api, so above that line the design's own
 * answer does not exist and this refuses rather than grinding through thousands of
 * sequential requests in a browser tab.
 */
const MAX_IMPORT_ROWS = 200;

/** Minimal RFC 4180 reader — quoted fields, doubled quotes, CRLF or LF. */
function parseCsv(text: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = '';
    let quoted = false;
    for (let i = 0; i < text.length; i += 1) {
        const char = text[i];
        if (quoted) {
            if (char === '"') {
                if (text[i + 1] === '"') {
                    cell += '"';
                    i += 1;
                } else quoted = false;
            } else cell += char;
            continue;
        }
        if (char === '"') {
            quoted = true;
            continue;
        }
        if (char === ',') {
            row.push(cell);
            cell = '';
            continue;
        }
        if (char === '\r') continue;
        if (char === '\n') {
            row.push(cell);
            rows.push(row);
            row = [];
            cell = '';
            continue;
        }
        cell += char;
    }
    if (cell !== '' || row.length > 0) {
        row.push(cell);
        rows.push(row);
    }
    return rows.filter((entry) => entry.some((value) => value.trim() !== ''));
}

/** "Nguyễn Thị Vân" → first "Vân", last "Nguyễn Thị" (Vietnamese order). */
function splitName(fullName: string): { first_name: string; last_name: string } {
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { first_name: '', last_name: '' };
    if (parts.length === 1) return { first_name: parts[0], last_name: '' };
    return { first_name: parts[parts.length - 1], last_name: parts.slice(0, -1).join(' ') };
}

/** Guess a target from a header name, so the mapping step opens mostly correct. */
function guessTarget(header: string): ImportTarget {
    const key = header.trim().toLowerCase().replace(/[\s_-]+/g, '');
    if (['email', 'mail', 'diachiemail'].includes(key)) return 'email';
    if (['username', 'tendangnhap', 'taikhoan'].includes(key)) return 'username';
    if (['fullname', 'name', 'hovaten', 'hoten', 'tên'].includes(key)) return 'fullName';
    if (['level', 'levelname', 'vaitro', 'capdo'].includes(key)) return 'levelName';
    return 'ignore';
}

/**
 * EAA-1839 (frame 35c, "Nhập tài khoản từ CSV"): parse a CSV in the browser, let
 * the operator map its columns onto portal fields, show a per-row preview, then
 * apply the accepted rows.
 *
 * NO IMPORT ENDPOINT EXISTS, and this is not one pretending to. There is no
 * `POST .../accounts/import` and no background-job runner for it; what exists is
 * the two single writes this screen already uses — `POST /accounts` (create the
 * Keycloak identity) and `POST .../assignments/bulk-apply` (give it a Level in this
 * đơn vị). The apply step is therefore a SEQUENTIAL, CANCELLABLE run over those two
 * calls per row, capped at 200 rows for the reason `MAX_IMPORT_ROWS` records, with
 * a real per-row result list. Every request it makes is one an operator could make
 * by hand through frame 35e; nothing is batched behind a label that implies the
 * server did it atomically.
 *
 * WHAT THAT MEANS FOR FAILURE, said plainly in the UI rather than hidden: a run can
 * stop half-done. Rows already applied stay applied — there is no transaction to
 * roll back across two endpoints — so the result list, not a success toast, is the
 * record of what happened.
 *
 * THREE OF FRAME 35c's OPTIONS ARE INERT, each for a contract reason:
 *  - "Cập nhật tài khoản đã tồn tại (đối chiếu theo email)": there is no
 *    update-by-email write. `PATCH /accounts/{sub}` takes `enabled` only.
 *  - "Vô hiệu hoá tài khoản không có trong tệp": that is a diff against the whole
 *    branch plus a bulk disable; neither exists.
 *  - "Đơn vị" as a per-row column: `bulk-apply` is single-org by construction (see
 *    `useInlineLevelPicker`), so every row lands in the scope org. The frame's
 *    "3 dòng có đơn vị không khớp nhánh" case cannot arise because the column is
 *    not offered.
 */
export function useImportAccountsCsv(vm: ScopedAccountsVM) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const toast = useToast();

    const [open, setOpen] = useState(false);
    const [step, setStep] = useState<ImportStep>('upload');
    const [fileName, setFileName] = useState('');
    const [fileSize, setFileSize] = useState(0);
    const [headers, setHeaders] = useState<string[]>([]);
    const [dataRows, setDataRows] = useState<string[][]>([]);
    const [mapping, setMapping] = useState<ImportTarget[]>([]);
    const [defaultLevelId, setDefaultLevelId] = useState('');
    const [sendInvite, setSendInvite] = useState(true);
    const [parseError, setParseError] = useState<string | null>(null);

    const [running, setRunning] = useState(false);
    const [results, setResults] = useState<ImportRowResult[]>([]);
    const cancelRef = useRef(false);

    const reset = useCallback(() => {
        setStep('upload');
        setFileName('');
        setFileSize(0);
        setHeaders([]);
        setDataRows([]);
        setMapping([]);
        setDefaultLevelId('');
        setSendInvite(true);
        setParseError(null);
        setResults([]);
        setRunning(false);
        cancelRef.current = false;
    }, []);

    const openModal = useCallback(() => {
        reset();
        setOpen(true);
    }, [reset]);

    const closeModal = useCallback(() => {
        cancelRef.current = true;
        setOpen(false);
    }, []);

    const acceptFile = useCallback(async (file: File) => {
        setParseError(null);
        const text = await file.text();
        const rows = parseCsv(text);
        if (rows.length < 2) {
            setParseError(t('console.hierarchy.scopedAccounts.import.errorNoRows'));
            return;
        }
        setFileName(file.name);
        setFileSize(file.size);
        setHeaders(rows[0]);
        setDataRows(rows.slice(1));
        setMapping(rows[0].map(guessTarget));
        setStep('map');
    }, [t]);

    const setColumnTarget = useCallback((index: number, target: ImportTarget) => {
        setMapping((prev) => prev.map((entry, i) => (i === index ? target : entry)));
    }, []);

    const levelByName = useMemo(() => {
        const map = new Map<string, string>();
        for (const level of vm.activeLevels) {
            map.set(level.name.trim().toLowerCase(), level.id);
            map.set(level.role_key.trim().toLowerCase(), level.id);
        }
        return map;
    }, [vm.activeLevels]);

    const plan = useMemo<ImportRowPlan[]>(() => {
        const emailIndex = mapping.indexOf('email');
        const nameIndex = mapping.indexOf('fullName');
        const usernameIndex = mapping.indexOf('username');
        const levelIndex = mapping.indexOf('levelName');
        const seenUsernames = new Set<string>();

        return dataRows.map((row, index) => {
            const email = (emailIndex >= 0 ? row[emailIndex] : '')?.trim() ?? '';
            const fullName = (nameIndex >= 0 ? row[nameIndex] : '')?.trim() ?? '';
            const rawUsername = (usernameIndex >= 0 ? row[usernameIndex] : '')?.trim() ?? '';
            const levelName = (levelIndex >= 0 ? row[levelIndex] : '')?.trim() ?? '';
            // Frame 35c's own rule: a blank Level cell falls back to the default
            // chosen in the mapping step, which may itself be "leave unassigned".
            const levelId = levelName
                ? levelByName.get(levelName.toLowerCase()) ?? null
                : defaultLevelId || null;

            const username = rawUsername || (email ? email.split('@')[0] : '');
            const problems: string[] = [];
            if (!email) problems.push(t('console.hierarchy.scopedAccounts.import.problemNoEmail'));
            else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
                problems.push(t('console.hierarchy.scopedAccounts.import.problemBadEmail'));
            }
            if (!username) {
                problems.push(t('console.hierarchy.scopedAccounts.import.problemNoUsername'));
            } else if (seenUsernames.has(username.toLowerCase())) {
                problems.push(t('console.hierarchy.scopedAccounts.import.problemDuplicate'));
            } else seenUsernames.add(username.toLowerCase());
            if (levelName && !levelByName.has(levelName.toLowerCase())) {
                problems.push(
                    t('console.hierarchy.scopedAccounts.import.problemUnknownLevel', {
                        level: levelName,
                    })
                );
            }

            return {
                lineNumber: index + 2,
                fullName,
                email,
                username,
                levelName,
                levelId,
                problems,
            };
        });
    }, [dataRows, mapping, levelByName, defaultLevelId, t]);

    const acceptedRows = useMemo(() => plan.filter((row) => row.problems.length === 0), [plan]);
    const rejectedCount = plan.length - acceptedRows.length;
    const overCap = acceptedRows.length > MAX_IMPORT_ROWS;

    const mappingError = useMemo(() => {
        if (!mapping.includes('email')) {
            return t('console.hierarchy.scopedAccounts.import.errorEmailUnmapped');
        }
        return null;
    }, [mapping, t]);

    const run = useCallback(async () => {
        if (!vm.orgId || !accessToken || overCap) return;
        cancelRef.current = false;
        setRunning(true);
        setResults([]);
        const collected: ImportRowResult[] = [];
        for (const row of acceptedRows) {
            if (cancelRef.current) break;
            try {
                const { first_name, last_name } = splitName(row.fullName);
                const issued = await issueAccount(config.apiBase, accessToken, {
                    username: row.username,
                    email: row.email,
                    ...(first_name ? { first_name } : {}),
                    ...(last_name ? { last_name } : {}),
                    credential_mode: sendInvite ? 'invite' : 'temp_password',
                });
                if (row.levelId) {
                    await applyBulkLevelAssign(config.apiBase, accessToken, vm.orgId, {
                        userSubs: [issued.sub],
                        levelId: row.levelId,
                    });
                }
                collected.push({
                    lineNumber: row.lineNumber,
                    username: row.username,
                    ok: true,
                    message: row.levelId
                        ? t('console.hierarchy.scopedAccounts.import.rowCreatedWithLevel')
                        : t('console.hierarchy.scopedAccounts.import.rowCreatedNoLevel'),
                });
            } catch (error) {
                collected.push({
                    lineNumber: row.lineNumber,
                    username: row.username,
                    ok: false,
                    message:
                        error instanceof OrgAdminApiError
                            ? error.message
                            : t('console.hierarchy.scopedAccounts.import.rowFailed'),
                });
            }
            setResults([...collected]);
        }
        setRunning(false);
        vm.invalidateList();
        const okCount = collected.filter((entry) => entry.ok).length;
        if (okCount > 0) {
            toast.success(
                t('console.hierarchy.scopedAccounts.import.doneToast', { count: okCount })
            );
        }
    }, [vm, accessToken, config.apiBase, acceptedRows, sendInvite, overCap, toast, t]);

    const cancelRun = useCallback(() => {
        cancelRef.current = true;
    }, []);

    return {
        open,
        openModal,
        closeModal,
        step,
        setStep,

        fileName,
        fileSize,
        parseError,
        acceptFile,

        headers,
        mapping,
        setColumnTarget,
        mappingError,
        defaultLevelId,
        setDefaultLevelId,
        sendInvite,
        setSendInvite,

        plan,
        acceptedRows,
        rejectedCount,
        overCap,
        maxRows: MAX_IMPORT_ROWS,

        running,
        results,
        run,
        cancelRun,
    };
}

export type ImportAccountsCsvVM = ReturnType<typeof useImportAccountsCsv>;
